import axios from "axios";

const SESSION_KEY = "omnimark_supabase_session";
const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
// A password grant has already been accepted by Supabase and hydrated by
// storeAuthResponse.  Do not immediately make a second identity request while
// the protected route and its first API requests mount at the same time.
// Later page loads still validate/refresh the stored session normally.
let freshSignInSession = null;
let runtimeSession = null;

function readStoredSession() {
  try {
    const local = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    if (local?.access_token) return local;
  } catch {}
  try {
    const temporary = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    if (temporary?.access_token) return temporary;
  } catch {}
  return null;
}

function readSession() {
  // Supabase rotates refresh tokens. A different OmniMark tab may already
  // have stored the rotated session, so persisted storage must win over this
  // tab's in-memory copy before attempting another refresh.
  const stored = readStoredSession();
  if (stored?.access_token) {
    runtimeSession = stored;
    return stored;
  }
  return runtimeSession;
}

function clearLegacyClientContext() {
  // These shared selection hints predate account scoping. They are not assets
  // or server records and must never carry a previous person's selection.
  for (const key of ["active_workspace", "selected_avatar_id", "selected_avatar_name", "selected_avatar_image_url", "selected_avatar", "omnimark_selected_avatar"]) {
    try { localStorage.removeItem(key); } catch {}
  }
}

function sessionIdentity(value) {
  return value?.email?.trim().toLowerCase() || value?.user?.email?.trim().toLowerCase() || null;
}

function writeSession(value) {
  const identity = sessionIdentity(value);
  let contextOwner = null;
  try { contextOwner = localStorage.getItem("omnimark_client_context_owner_v2"); } catch {}
  // The marker also handles the first restore after upgrading an already
  // signed-in browser: old shared hints have no trustworthy account owner.
  if (contextOwner !== identity || sessionIdentity(readSession()) !== identity) clearLegacyClientContext();
  try { localStorage.setItem("omnimark_client_context_owner_v2", identity || ""); } catch {}
  // The current SPA must be able to attach the accepted grant immediately,
  // even in an embedded browser that delays or restricts storage writes.
  runtimeSession = value;
  const serialized = JSON.stringify(value);
  try { localStorage.setItem(SESSION_KEY, serialized); } catch {}
  try { sessionStorage.setItem(SESSION_KEY, serialized); } catch {}
}

function clearSession() {
  runtimeSession = null;
  clearLegacyClientContext();
  try { localStorage.removeItem("omnimark_client_context_owner_v2"); } catch {}
  try { localStorage.removeItem(SESSION_KEY); } catch {}
  try { sessionStorage.removeItem(SESSION_KEY); } catch {}
}

if (typeof window !== "undefined" && window.addEventListener) {
  window.addEventListener("storage", (event) => {
    if (event.key !== SESSION_KEY && event.key !== null) return;
    let previous = null;
    let next = null;
    try { previous = JSON.parse(event.oldValue || "null"); } catch {}
    try { next = JSON.parse(event.newValue || "null"); } catch {}
    if (event.key !== null && sessionIdentity(previous) === sessionIdentity(next)) return;
    // Another tab changed accounts or signed out. Do not keep its previous
    // account's in-memory UI/query state or fallback session. Never erase the
    // new shared session belonging to the account that just signed in.
    runtimeSession = null;
    freshSignInSession = null;
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
    window.location.assign("/login");
  });
}

async function config() {
  const { data } = await axios.get(`${backendUrl}/api/auth/config`);
  if (!data?.url || !data?.anonKey) throw new Error("Secure sign-in is not configured yet.");
  return data;
}

async function fetchUser(session, authConfig) {
  const response = await fetch(`${authConfig.url}/auth/v1/user`, {
    headers: { apikey: authConfig.anonKey, Authorization: `Bearer ${session.access_token}` },
  });
  if (!response.ok) throw new Error("Your sign-in session is no longer valid.");
  const user = await response.json();
  return { ...session, email: user.email?.trim().toLowerCase(), userId: user.id };
}

async function storeAuthResponse(result) {
  if (!result?.access_token || !result?.refresh_token) throw new Error("No active session was returned by Supabase.");
  const hydrated = await fetchUser(result, await config());
  writeSession(hydrated);
  return hydrated;
}

function storePasswordGrant(result) {
  if (!result?.access_token || !result?.refresh_token) throw new Error("No active session was returned by Supabase.");
  const email = result.user?.email?.trim().toLowerCase();
  if (!email) throw new Error("Supabase did not return the signed-in account identity.");
  const session = { ...result, email, userId: result.user?.id || null };
  writeSession(session);
  return session;
}

export const auth = {
  async signIn(email, password) {
    const authConfig = await config();
    const response = await fetch(`${authConfig.url}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: { apikey: authConfig.anonKey, "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result?.error_description || result?.msg || "Unable to sign in.");
    // The password grant is already authenticated by Supabase and includes the
    // verified user. Avoid a second /user request here: it was the request
    // causing successful grants to bounce back to the login screen.
    const session = storePasswordGrant(result);
    freshSignInSession = session;
    return session;
  },

  async requestPasswordRecovery(email) {
    const authConfig = await config();
    const response = await fetch(`${authConfig.url}/auth/v1/recover`, {
      method: "POST",
      headers: { apikey: authConfig.anonKey, "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        redirect_to: `${window.location.origin}/auth/callback`,
      }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 429 || /rate limit/i.test(result?.message || result?.msg || "")) {
        throw new Error("Too many recovery emails were requested. Please wait before trying again.");
      }
      throw new Error(result?.error_description || result?.message || result?.msg || "Unable to send a recovery email.");
    }
  },

  async restore() {
    const current = readSession();
    if (!current?.access_token) return null;
    if (freshSignInSession?.access_token === current.access_token) return current;
    try {
      const authConfig = await config();
      const expiresSoon = !current.expires_at || current.expires_at * 1000 < Date.now() + 60_000;
      // The backend verifies the JWT on every protected request. Avoid a
      // redundant browser-side identity request for a still-valid token.
      if (!expiresSoon) return current;
      if (expiresSoon && current.refresh_token) {
        const response = await fetch(`${authConfig.url}/auth/v1/token?grant_type=refresh_token`, {
          method: "POST",
          headers: { apikey: authConfig.anonKey, "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: current.refresh_token }),
        });
        if (!response.ok) throw new Error("Session refresh failed.");
        return storeAuthResponse(await response.json());
      }
      const hydrated = await fetchUser(current, authConfig);
      writeSession(hydrated);
      return hydrated;
    } catch {
      // A simultaneous tab can legitimately lose the refresh-token race.
      // If that tab stored the rotated session, keep it instead of clearing
      // every OmniMark tab and making the owner appear signed out.
      const rotated = readStoredSession();
      if (rotated?.access_token && rotated.access_token !== current.access_token) {
        runtimeSession = rotated;
        return rotated;
      }
      clearSession();
      return null;
    }
  },

  async completeCallback() {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const access_token = params.get("access_token");
    const refresh_token = params.get("refresh_token");
    if (!access_token || !refresh_token) throw new Error("This sign-in link is missing a valid session.");
    return storeAuthResponse({
      access_token,
      refresh_token,
      expires_at: Number(params.get("expires_at") || 0),
      expires_in: Number(params.get("expires_in") || 0),
      token_type: params.get("token_type") || "bearer",
    });
  },

  async setPassword(password) {
    const session = await this.restore();
    if (!session?.access_token) throw new Error("Open your invitation link again to set a password.");
    const authConfig = await config();
    const response = await fetch(`${authConfig.url}/auth/v1/user`, {
      method: "PUT",
      headers: {
        apikey: authConfig.anonKey,
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ password }),
    });
    if (!response.ok) throw new Error("Unable to set your password.");
  },

  async getAccessToken() {
    const current = readSession();
    if (current?.access_token && current.expires_at * 1000 >= Date.now() + 60_000) return current.access_token;
    return (await this.restore())?.access_token || null;
  },

  getEmail() {
    return readSession()?.email || null;
  },

  clear() {
    freshSignInSession = null;
    clearSession();
  },
};
